import React, { useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import {
  Typography,
  Alert,
  Box,
  Container,
  Grid,
  InputAdornment,
  TextField,
  Chip,
  CircularProgress,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import ListView from "./ListView";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "@mui/icons-material";
import { connect } from "react-redux";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import PetsIcon from "@mui/icons-material/Pets";

const Categories = ({ categoriesRaw, loading, error }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState();
  const [view, setView] = useState("Grid");
  const [search, setSearch] = useState("");
  const [snackbarStates, setSnackbarStates] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    setCategories(categoriesRaw);
  }, [loading, categoriesRaw]);

  useEffect(() => {
    if (error && error !== "") {
      setSnackbarStates({ open: true, message: error, severity: "error" });
    }
  }, [error]);

  const filteredCategories = categories?.filter((item) =>
    search === "" ? true : item.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <meta property="og:image" content="https://prodappdata.s3.amazonaws.com/preview-images/classes-preview.png" />
        <meta property="og:title" content="Classes" />
        <meta property="og:description" content="Moore Exotics Classes" />
      </Helmet>

      <Snackbar
        open={snackbarStates.open}
        autoHideDuration={3000}
        onClose={() => setSnackbarStates({ ...snackbarStates, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert variant="filled" severity={snackbarStates.severity} sx={{ width: "100%" }}>
          {snackbarStates.message}
        </Alert>
      </Snackbar>

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>
        {/* Hero Header */}
        <Box
          sx={{
            position: "relative",
            background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)",
            borderBottom: "1px solid #222",
            py: { xs: 5, md: 7 },
            overflow: "hidden",
          }}
        >
          {/* Subtle decorative bg */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Typography
                component={Link}
                to="/"
                sx={{
                  color: "#777",
                  fontSize: "13px",
                  textDecoration: "none",
                  "&:hover": { color: "#FFC001" },
                }}
              >
                Home
              </Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>
                Classes
              </Typography>
            </Box>

            {/* Title */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "28px", sm: "36px", md: "48px" },
                  color: "#fff",
                  lineHeight: 1.1,
                  mb: 1,
                }}
              >
                Browse{" "}
                <Box component="span" sx={{ color: "#FFC001" }}>
                  Classes
                </Box>
              </Typography>
              <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" } }}>
                Explore exotic animals, plants & minerals by category
              </Typography>
            </Box>

            {/* Search + view toggle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <TextField
                placeholder="Search classes..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: "420px" },
                  backgroundColor: "#1a1a1a",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-root": {
                    paddingRight: 0,
                    color: "#fff",
                    borderRadius: "10px",
                    "& fieldset": { borderColor: "#333" },
                    "&:hover fieldset": { borderColor: "#FFC001" },
                    "&.Mui-focused fieldset": { borderColor: "#FFC001" },
                  },
                  "& .MuiInputBase-input::placeholder": { color: "#666", opacity: 1 },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          backgroundColor: "#FFC001",
                          height: "40px",
                          width: "44px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderTopRightRadius: "10px",
                          borderBottomRightRadius: "10px",
                          cursor: "pointer",
                        }}
                      >
                        <Search sx={{ color: "#000", fontSize: 20 }} />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #333",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <IconButton
                  onClick={() => setView("Grid")}
                  sx={{
                    borderRadius: 0,
                    px: 1.5,
                    backgroundColor: view === "Grid" ? "#FFC001" : "transparent",
                    "&:hover": { backgroundColor: view === "Grid" ? "#e6ac00" : "#2a2a2a" },
                  }}
                >
                  <GridViewIcon sx={{ color: view === "Grid" ? "#000" : "#888", fontSize: 20 }} />
                </IconButton>
                <IconButton
                  onClick={() => setView("List")}
                  sx={{
                    borderRadius: 0,
                    px: 1.5,
                    backgroundColor: view === "List" ? "#FFC001" : "transparent",
                    "&:hover": { backgroundColor: view === "List" ? "#e6ac00" : "#2a2a2a" },
                  }}
                >
                  <ListIcon sx={{ color: view === "List" ? "#000" : "#888", fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Result count */}
            {!loading && filteredCategories && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography sx={{ color: "#555", fontSize: "13px" }}>
                  {filteredCategories.length} classe{filteredCategories.length !== 1 ? "s" : ""}
                  {search ? ` for "${search}"` : ""}
                </Typography>
              </Box>
            )}
          </Container>
        </Box>

        {/* Content */}
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: "#FFC001" }} />
            </Box>
          ) : filteredCategories?.length > 0 ? (
            view === "Grid" ? (
              <Grid container spacing={2.5}>
                {filteredCategories.map((item, index) => (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={item.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.04 }}
                    >
                      <Box
                        onClick={() => item.active && navigate(`/classes/${item.id}/breeds`)}
                        sx={{
                          cursor: item.active ? "pointer" : "default",
                          borderRadius: "16px",
                          overflow: "hidden",
                          position: "relative",
                          height: { xs: "150px", sm: "180px", md: "200px" },
                          border: "1px solid #2a2a2a",
                          transition: "all 0.3s ease",
                          opacity: item.active ? 1 : 0.75,
                          ...(item.active && {
                            "&:hover": {
                              borderColor: "#FFC001",
                              transform: "translateY(-5px)",
                              boxShadow: "0 12px 30px rgba(255,192,1,0.15)",
                              "& .cat-bg": {
                                filter: "blur(5px) brightness(0.3)",
                                transform: "scale(1.1)",
                              },
                              "& .cat-label": { bottom: "14px" },
                              "& .cat-badge": { opacity: 1, transform: "translateY(0)" },
                            },
                          }),
                        }}
                      >
                        {/* Background image */}
                        <Box
                          className="cat-bg"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: item.image ? `url(${item.image})` : "none",
                            backgroundColor: item.image ? "transparent" : "#1a1a1a",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "blur(0px) brightness(0.55)",
                            transform: "scale(1.0)",
                            transition: "all 0.4s ease",
                          }}
                        />

                        {/* Gradient overlay */}
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)",
                          }}
                        />

                        {/* Gold top line */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            backgroundColor: "#FFC001",
                            opacity: item.active ? 0.9 : 0.3,
                          }}
                        />

                        {/* Fallback icon */}
                        {!item.image && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PetsIcon sx={{ fontSize: 40, color: "#FFC001", opacity: 0.4 }} />
                          </Box>
                        )}

                        {/* Coming Soon chip */}
                        {!item.active && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 10,
                              backgroundColor: "#FFC001",
                              color: "#000",
                              fontSize: "10px",
                              fontWeight: 700,
                              px: 1,
                              py: 0.3,
                              borderRadius: "6px",
                              letterSpacing: "0.5px",
                            }}
                          >
                            SOON
                          </Box>
                        )}

                        {/* Brand promo badge */}
                        {item.brand_promotions?.length > 0 && (
                          <Box
                            component="img"
                            src={item.brand_promotions[0].image}
                            alt={item.brand_promotions[0].name}
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 10,
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              border: "2px solid #FFC001",
                              objectFit: "contain",
                              backgroundColor: "#000",
                            }}
                          />
                        )}

                        {/* Name label */}
                        <Box
                          className="cat-label"
                          sx={{
                            position: "absolute",
                            bottom: "10px",
                            left: 0,
                            right: 0,
                            px: 1.5,
                            transition: "bottom 0.3s ease",
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: { xs: "13px", sm: "14px", md: "15px" },
                              textShadow: "0 1px 6px rgba(0,0,0,0.9)",
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Box>

                        {/* "Explore" badge on hover */}
                        <Box
                          className="cat-badge"
                          sx={{
                            position: "absolute",
                            bottom: "36px",
                            left: "50%",
                            transform: "translateX(-50%) translateY(8px)",
                            opacity: 0,
                            transition: "all 0.3s ease",
                            backgroundColor: "#FFC001",
                            color: "#000",
                            fontWeight: 700,
                            fontSize: "11px",
                            px: 1.5,
                            py: 0.4,
                            borderRadius: "20px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Explore →
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <ListView categories={categories} page="Classes" search={search} />
            )
          ) : (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <PetsIcon sx={{ fontSize: 60, color: "#333", mb: 2 }} />
              <Typography sx={{ color: "#555", fontSize: "16px" }}>
                No classes found{search ? ` for "${search}"` : ""}.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = ({ Categories }) => ({
  categoriesRaw: Categories.classes,
  loading: Categories.loading,
  error: Categories.error,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Categories);
